import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search, Filter, Trash2, Calendar, Pencil, User, PiggyBank, Users, SplitSquareHorizontal, ChevronDown, ChevronUp, Info, Upload, FileText, FileSpreadsheet } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getTransactions, deleteTransaction, getFamily, getInstallmentGroups, deleteInstallmentGroup } from '../lib/api';
import { formatCurrency, formatDate, getCategoryColor } from '../lib/utils';
import QuickAddModal from '../components/QuickAddModal';
import EditTransactionModal from '../components/EditTransactionModal';
import ImportCSVModal from '../components/ImportCSVModal';
import BankImportModal from '../components/BankImportModal';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

const CATEGORIES = [
  'logement', 'transport', 'alimentation', 'restaurant', 'abonnements', 'shopping',
  'loisirs', 'sante', 'epargne', 'education', 'animaux',
  'enfants', 'cadeaux', 'entretien', 'professionnel', 'divers'
];

// Generate last 12 months for selector
const getLast12Months = (language) => {
  const months = [];
  const now = new Date();
  const monthNames = {
    fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    pt: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
  };
  const names = monthNames[language] || monthNames.fr;
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `month_${date.getFullYear()}_${date.getMonth()}`,
      label: `${names[date.getMonth()]} ${date.getFullYear()}`,
      year: date.getFullYear(),
      month: date.getMonth()
    });
  }
  return months;
};

// Period filter options
const getPeriodDates = (period) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  // Handle specific month selection (format: month_YYYY_M)
  if (period.startsWith('month_')) {
    const parts = period.split('_');
    const year = parseInt(parts[1]);
    const month = parseInt(parts[2]);
    const lastDay = new Date(year, month + 1, 0).getDate();
    return {
      start: new Date(year, month, 1).toISOString(),
      end: new Date(year, month, lastDay, 23, 59, 59).toISOString()
    };
  }
  
  switch (period) {
    case 'current_month':
      return {
        start: new Date(currentYear, currentMonth, 1).toISOString(),
        end: null
      };
    case 'last_month':
      return {
        start: new Date(currentYear, currentMonth - 1, 1).toISOString(),
        end: new Date(currentYear, currentMonth, 0, 23, 59, 59).toISOString()
      };
    case 'T1':
      return {
        start: new Date(currentYear, 0, 1).toISOString(),
        end: new Date(currentYear, 2, 31, 23, 59, 59).toISOString()
      };
    case 'T2':
      return {
        start: new Date(currentYear, 3, 1).toISOString(),
        end: new Date(currentYear, 5, 30, 23, 59, 59).toISOString()
      };
    case 'T3':
      return {
        start: new Date(currentYear, 6, 1).toISOString(),
        end: new Date(currentYear, 8, 30, 23, 59, 59).toISOString()
      };
    case 'T4':
      return {
        start: new Date(currentYear, 9, 1).toISOString(),
        end: new Date(currentYear, 11, 31, 23, 59, 59).toISOString()
      };
    default: // 'all'
      return { start: null, end: null };
  }
};

export default function TransactionsPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { shouldShow: showTutorial } = useTutorial();
  
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all'); // attributed_member_id filter
  const [budgetTypeFilter, setBudgetTypeFilter] = useState('all'); // all, common, personal
  const [periodFilter, setPeriodFilter] = useState('all'); // all, current_month, last_month, T1, T2, T3, T4
  const [family, setFamily] = useState(null);
  const [installmentGroups, setInstallmentGroups] = useState([]);
  const [showInstallments, setShowInstallments] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [showBankImport, setShowBankImport] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);

  useEffect(() => {
    loadTransactions();
    loadInstallmentGroups();
    if (user?.family_id) {
      loadFamily();
    }
  }, [categoryFilter, budgetTypeFilter, periodFilter, user?.family_id]);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  const loadFamily = async () => {
    try {
      const res = await getFamily();
      setFamily(res.data.family);
    } catch (error) {
      console.error('Failed to load family:', error);
    }
  };

  const loadInstallmentGroups = async () => {
    try {
      const res = await getInstallmentGroups();
      setInstallmentGroups(res.data.installment_groups || []);
    } catch (error) {
      console.error('Failed to load installment groups:', error);
    }
  };

  const handleDeleteInstallmentGroup = async (groupId, description) => {
    const confirmMsg = t('delete_all_installments').replace('{description}', description);
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await deleteInstallmentGroup(groupId);
      toast.success(t('installments_deleted'));
      loadTransactions();
      loadInstallmentGroups();
    } catch (error) {
      console.error('Failed to delete installment group:', error);
      toast.error(t('failed_to_delete'));
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const params = { limit: 500 }; // Increase limit for period filtering
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      if (budgetTypeFilter !== 'all') {
        params.budget_type = budgetTypeFilter;
      }
      // Apply period filter
      if (periodFilter !== 'all') {
        const { start, end } = getPeriodDates(periodFilter);
        if (start) params.start_date = start;
        if (end) params.end_date = end;
      }
      const response = await getTransactions(params);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm(t('delete_transaction_confirm'))) {
      return;
    }
    try {
      await deleteTransaction(transactionId);
      loadTransactions();
    } catch (error) {
      console.error('Failed to delete transaction:', error);
    }
  };

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedTransaction(null);
    loadTransactions();
  };

  const filteredTransactions = transactions.filter(txn => {
    // Member filter (multi-attribution OR legacy for_member_id)
    if (memberFilter !== 'all') {
      const attributed = txn.attributed_member_ids || [];
      const matches = attributed.includes(memberFilter) || txn.for_member_id === memberFilter;
      if (!matches) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      txn.description?.toLowerCase().includes(query) ||
      txn.category.toLowerCase().includes(query)
    );
  });

  // Group transactions by date
  const groupedTransactions = filteredTransactions.reduce((groups, txn) => {
    const date = txn.date.split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(txn);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
          {/* Title row */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">{t('transactions')}</h1>
          </div>
          
          {/* Search, Filter & Actions row */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[150px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            {/* Period Filter */}
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="period-filter">
                <Calendar className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline"><SelectValue /></span>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">{language === 'fr' ? 'Toutes' : language === 'pt' ? 'Todas' : 'All'}</SelectItem>
                <SelectItem value="current_month">{language === 'fr' ? 'Ce mois' : language === 'pt' ? 'Este mês' : 'This month'}</SelectItem>
                <SelectItem value="last_month">{language === 'fr' ? 'Mois dernier' : language === 'pt' ? 'Mês passado' : 'Last month'}</SelectItem>
                {/* Separator */}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                  {language === 'fr' ? 'Trimestres' : language === 'pt' ? 'Trimestres' : 'Quarters'}
                </div>
                <SelectItem value="T1">{language === 'fr' ? 'T1 (Jan-Mar)' : language === 'pt' ? 'T1 (Jan-Mar)' : 'Q1 (Jan-Mar)'}</SelectItem>
                <SelectItem value="T2">{language === 'fr' ? 'T2 (Avr-Juin)' : language === 'pt' ? 'T2 (Abr-Jun)' : 'Q2 (Apr-Jun)'}</SelectItem>
                <SelectItem value="T3">{language === 'fr' ? 'T3 (Juil-Sep)' : language === 'pt' ? 'T3 (Jul-Set)' : 'Q3 (Jul-Sep)'}</SelectItem>
                <SelectItem value="T4">{language === 'fr' ? 'T4 (Oct-Déc)' : language === 'pt' ? 'T4 (Out-Dez)' : 'Q4 (Oct-Dec)'}</SelectItem>
                {/* Separator - Specific Months */}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                  {language === 'fr' ? 'Mois spécifique' : language === 'pt' ? 'Mês específico' : 'Specific month'}
                </div>
                {getLast12Months(language).map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[100px] sm:w-[140px]" data-testid="category-filter">
                <Filter className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline"><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{t(`cat_${cat}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Member Filter (shared/hybrid family only) */}
            {family && ['shared', 'hybrid'].includes(family.budget_mode) && family.members && family.members.length > 1 && (
              <Select value={memberFilter} onValueChange={setMemberFilter}>
                <SelectTrigger className="w-[110px] sm:w-[150px]" data-testid="member-filter">
                  <span className="text-xs sm:text-sm truncate"><SelectValue placeholder={t('filter_by_member')} /></span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all_members')}</SelectItem>
                  {family.members.map(m => (
                    <SelectItem key={m.member_id} value={m.member_id} data-testid={`member-filter-${m.member_id}`}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="sm:hidden"
                  data-testid="import-btn-mobile"
                >
                  <Upload className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowBankImport(true)} data-testid="import-bank-option-mobile">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('import_bank_statement') || 'Relevé bancaire'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowImportCSV(true)} data-testid="import-csv-option-mobile">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="hidden sm:flex"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {t('import')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowBankImport(true)} data-testid="import-bank-option">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('import_bank_statement') || 'Relevé bancaire'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowImportCSV(true)} data-testid="import-csv-option">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              size="icon"
              className="sm:hidden bg-primary"
              onClick={() => setShowQuickAdd(true)} 
              data-testid="add-transaction-btn-mobile"
            >
              <Plus className="w-4 h-4" />
            </Button>
            <Button 
              className="hidden sm:flex"
              onClick={() => setShowQuickAdd(true)} 
              data-testid="add-transaction-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('add')}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Budget Type Filter for Hybrid Mode */}
        {family?.budget_mode === 'hybrid' && (
          <div className="mb-4 flex gap-2" data-testid="budget-type-filter">
            <Button
              variant={budgetTypeFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setBudgetTypeFilter('all')}
              className="flex-1"
            >
              {t('all')}
            </Button>
            <Button
              variant={budgetTypeFilter === 'common' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setBudgetTypeFilter('common')}
              className="flex-1 gap-2"
            >
              <Users className="w-4 h-4" />
              {t('common_transactions')}
            </Button>
            <Button
              variant={budgetTypeFilter === 'personal' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setBudgetTypeFilter('personal')}
              className="flex-1 gap-2"
            >
              <User className="w-4 h-4" />
              {t('personal_transactions')}
            </Button>
          </div>
        )}

        {/* Installment Payments Section */}
        {installmentGroups.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <button
              onClick={() => setShowInstallments(!showInstallments)}
              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 rounded-xl border border-purple-200 dark:border-purple-800"
              data-testid="installments-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <SplitSquareHorizontal className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium">
                    {t('active_installments')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {installmentGroups.length} {t('purchase_count')}
                  </p>
                </div>
              </div>
              {showInstallments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            <AnimatePresence>
              {showInstallments && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 space-y-3">
                    {installmentGroups.map((group) => (
                      <Card key={group.installment_group_id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium">{group.source_description || 'Achat'}</span>
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                  {group.total_installments}x
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-muted-foreground">
                                  {t('total_label')} <span className="font-medium">{formatCurrency(group.original_amount)}</span>
                                </span>
                                <span className="text-muted-foreground">
                                  {t('monthly_label')} <span className="font-medium">{formatCurrency(group.installment_amount)}</span>
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-green-600 font-medium">
                                    {group.paid_installments}/{group.total_installments} {t('paid')}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {group.remaining_installments} {t('remaining')}
                                  </span>
                                </div>
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all"
                                    style={{ width: `${(group.paid_installments / group.total_installments) * 100}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteInstallmentGroup(group.installment_group_id, group.source_description)}
                              className="text-muted-foreground hover:text-destructive ml-4"
                              data-testid={`delete-installment-${group.installment_group_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : filteredTransactions.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedTransactions).map(([date, txns]) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {formatDate(date, language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ({txns.length} {t('transaction_count')})
                  </span>
                </div>
                <Card>
                  <CardContent className="p-0 divide-y divide-border">
                    {txns.map((txn) => {
                      const isOwnTransaction = txn.user_id === user?.user_id;
                      const isCommonPot = txn.budget_type === 'common';
                      const showBadge = family && (family.budget_mode === 'hybrid' || family.budget_mode === 'shared');
                      const isInstallment = txn.is_installment;
                      
                      return (
                      <div key={txn.transaction_id} className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center relative"
                            style={{ backgroundColor: `${getCategoryColor(txn.category)}20` }}
                          >
                            <span className="text-lg">
                              {txn.category === 'alimentation' ? '🍔' :
                               txn.category === 'transport' ? '🚗' :
                               txn.category === 'shopping' ? '🛍️' :
                               txn.category === 'loisirs' ? '🎮' :
                               txn.category === 'logement' ? '🏠' :
                               txn.category === 'abonnements' ? '📱' :
                               txn.category === 'sante' ? '💊' :
                               txn.category === 'argent_de_poche' ? '👛' : '💰'}
                            </span>
                            {/* Installment indicator on icon */}
                            {isInstallment && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                                <span className="text-[8px] text-white font-bold">{txn.installment_number}</span>
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{txn.description || t(`cat_${(txn.category || 'autre').toLowerCase()}`)}</p>
                              {/* Installment badge */}
                              {isInstallment && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  <SplitSquareHorizontal className="w-3 h-3" />
                                  {txn.installment_number}/{txn.total_installments}
                                </span>
                              )}
                              {/* Budget type badge for hybrid mode */}
                              {family?.budget_mode === 'hybrid' && isCommonPot && (
                                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {t('common')}
                                </span>
                              )}
                              {family?.budget_mode === 'hybrid' && !isCommonPot && (
                                <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {t('personal')}
                                </span>
                              )}
                              {/* Show who made the transaction in shared mode */}
                              {family?.budget_mode === 'shared' && !isOwnTransaction && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {family.members?.find(m => m.user_id === txn.user_id)?.name?.split(' ')[0] || 'Membre'}
                                </span>
                              )}
                              {/* Attributed members badges (multi-attribution) */}
                              {txn.attributed_member_ids && txn.attributed_member_ids.length > 0 && family?.members && (
                                txn.attributed_member_ids.map(mid => {
                                  const m = family.members.find(fm => fm.member_id === mid);
                                  if (!m) return null;
                                  return (
                                    <span
                                      key={mid}
                                      className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-1.5 py-0.5 rounded-full"
                                      data-testid={`txn-attrib-badge-${mid}`}
                                      title={t('attributed_to_members')}
                                    >
                                      → {m.name?.split(' ')[0]}
                                    </span>
                                  );
                                })
                              )}
                              {/* Multiple payment methods indicator */}
                              {txn.payment_methods && txn.payment_methods.length > 1 && (
                                <span className="text-xs bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                  💳 {txn.payment_methods.length}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{t(`cat_${(txn.category || 'autre').toLowerCase()}`)}</p>
                            {/* Show payment methods breakdown if multiple */}
                            {txn.payment_methods && txn.payment_methods.length > 1 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {txn.payment_methods.map((pm, idx) => (
                                  <span key={idx} className="text-xs text-muted-foreground">
                                    {pm.type === 'card' ? '💳' : 
                                     pm.type === 'cash' ? '💵' : 
                                     pm.type === 'ticket_restaurant' ? '🍽️' : 
                                     pm.type === 'cheque_vacances' ? '🏖️' : 
                                     pm.type === 'cheque_cadeau' ? '🎁' : '📝'} 
                                    {pm.amount}€
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-destructive">-{formatCurrency(txn.amount)}</span>
                          {/* Only show edit/delete for own transactions */}
                          {isOwnTransaction && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleEdit(txn)}
                                className="text-muted-foreground hover:text-primary"
                                data-testid={`edit-txn-${txn.transaction_id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDelete(txn.transaction_id)}
                                className="text-muted-foreground hover:text-destructive"
                                data-testid={`delete-txn-${txn.transaction_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-medium mb-2">{t('no_transactions')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('start_tracking_expenses')}
            </p>
            <Button onClick={() => setShowQuickAdd(true)} data-testid="add-first-transaction-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('add_expense')}
            </Button>
          </div>
        )}
      </main>

      <QuickAddModal 
        open={showQuickAdd} 
        onClose={() => setShowQuickAdd(false)}
        onSuccess={() => {
          setShowQuickAdd(false);
          loadTransactions();
        }}
      />

      <EditTransactionModal
        transaction={selectedTransaction}
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTransaction(null);
        }}
        onSuccess={handleEditSuccess}
      />

      <ImportCSVModal 
        open={showImportCSV}
        onClose={() => setShowImportCSV(false)}
        onSuccess={() => {
          setShowImportCSV(false);
          loadTransactions();
        }}
      />

      <BankImportModal 
        open={showBankImport}
        onOpenChange={setShowBankImport}
        onImportComplete={() => {
          loadTransactions();
        }}
      />

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
